import {
	NAVBAR_BRANCH_QUERY_KEY_ROOT,
	type NavbarBranchItem,
	navbarBranchQueryKeys,
} from "@src/components/board/queries/fetch-ranklist";
import type { NavbarSection } from "@src/gql/graphql";
import type { QueryClient } from "@tanstack/react-query";

export function getFolderHasChildrenInBranchLists(
	queryClient: QueryClient,
	section: NavbarSection,
	folderKey: string,
): boolean | undefined {
	const queries = queryClient.getQueriesData<NavbarBranchItem[]>({
		queryKey: navbarBranchQueryKeys.section(section),
	});
	for (const [, data] of queries) {
		if (!data) continue;
		const item = data.find((i) => i.key === folderKey);
		if (item) return item.hasChildren;
	}
	return undefined;
}

/**
 * Updates a folder row's `hasChildren` in whichever branch list contains it
 * (the list for the folder's parent).
 */
export function setFolderHasChildrenInBranchList(
	queryClient: QueryClient,
	section: NavbarSection,
	folderKey: string,
	hasChildren: boolean,
): void {
	const queries = queryClient.getQueriesData<NavbarBranchItem[]>({
		queryKey: navbarBranchQueryKeys.section(section),
	});
	for (const [queryKey, data] of queries) {
		if (!data) continue;
		const idx = data.findIndex((i) => i.key === folderKey);
		if (idx === -1) continue;
		const item = data[idx];
		if (item.hasChildren === hasChildren) return;
		queryClient.setQueryData<NavbarBranchItem[]>(queryKey, (old) => {
			if (!old) return old;
			return old.map((i) => (i.key === folderKey ? { ...i, hasChildren } : i));
		});
		return;
	}
}

/**
 * For a branch list keyed by `navbarBranchQueryKeys.branch`, the list is the
 * children of `parentFolderKey`. Syncs that folder's `hasChildren` flag in its
 * parent's branch list from the cached child count.
 */
export function syncFolderHasChildrenFromBranchQueryKey(
	queryClient: QueryClient,
	section: NavbarSection,
	queryKey: readonly unknown[],
): void {
	if (queryKey[0] !== NAVBAR_BRANCH_QUERY_KEY_ROOT || queryKey.length < 3)
		return;
	const parentFolderKey = queryKey[2];
	if (parentFolderKey === "root") return;
	const data = queryClient.getQueryData<NavbarBranchItem[]>(queryKey);
	const count = data?.length ?? 0;
	setFolderHasChildrenInBranchList(
		queryClient,
		section,
		String(parentFolderKey),
		count > 0,
	);
}
